<!--
  Review banner on lead bots (created by the demo creation funnel), for moinAI
  admins only. Shows who the lead is and when the bot is released to them, with
  "Jetzt freigeben", "Zurückhalten" and "Fortsetzen". Gone once released.
-->
<script lang="ts">
import { reactive } from 'vue';
import type { DemoLeadReview } from '@/services/demo';

/**
 * Last known review per bot, shared by every page's banner: `GenericWrapper`
 * mounts a new one per page, which then shows this right away while it asks
 * the API again.
 */
const reviews = reactive<Record<string, DemoLeadReview>>({});
</script>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Info, Pause, Play, Send, UserRoundSearch } from '@lucide/vue';
import DemoService, { type DemoLeadStatus } from '@/services/demo';
import { useAuthStore } from '@/stores/auth';
import { useBotsStore } from '@/stores/bots';
import { stageTypes } from '@/stores/welcome';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { formatDateTimeShortMonth } from '@/utils/formatDateTime';
import LeadDetailsDialog from '@/components/leadBot/LeadDetailsDialog.vue';
import LeadReleaseDialog from '@/components/leadBot/LeadReleaseDialog.vue';

/** Before these, there is a release to review. After, the banner has nothing left to do. */
const unreleasedStatuses: DemoLeadStatus[] = ['submitted', 'building', 'review'];

const { t } = useI18n();
const authStore = useAuthStore();
const botsStore = useBotsStore();

const botId = computed(() => botsStore.currentBotId);
const isLeadBot = computed(() => authStore.isAdmin && botsStore.currentBotStage === stageTypes.LEAD);

const lead = computed(() => (botId.value ? reviews[botId.value] ?? null : null));
const visible = computed(() => isLeadBot.value && !!lead.value && unreleasedStatuses.includes(lead.value.leadStatus));

/** How often an open banner asks again, e.g. for the build being done, an automatic release or another admin's hold */
const REVIEW_POLL_INTERVAL_MS = 30 * 1000;

/**
 * Counts this banner's own changes, so a load that was already on its way when one came in doesn't bring back
 * what was before it.
 */
let revision = 0;

async function loadReview() {
  const id = botId.value;
  if (!isLeadBot.value || !id) return;

  const before = revision;
  const result = await DemoService.getLeadReview(id);
  if (result.status === 'OK' && before === revision) reviews[id] = result.lead;
}
watch([isLeadBot, botId], loadReview, { immediate: true });

// Only while there is something to review, and not while the tab is in the background or an action is running
let pollTimer: number | undefined;
onMounted(() => {
  pollTimer = window.setInterval(() => {
    if (visible.value && !updating.value && !document.hidden) loadReview();
  }, REVIEW_POLL_INTERVAL_MS);
});
onUnmounted(() => window.clearInterval(pollTimer));

const formatDate = (date: string | null) => formatDateTimeShortMonth(date) || '–';

const name = computed(() => [lead.value?.contact.firstName, lead.value?.contact.lastName].filter(Boolean).join(' '));
const goal = computed(() => (lead.value?.purpose ? t(`demoV3.goals.${lead.value.purpose}.title`) : null));
const statusText = computed(() => {
  if (!lead.value) return '';
  if (lead.value.leadStatus !== 'review') return 'Wird noch aufgebaut';
  if (lead.value.isPaused) return `Zurückgehalten von ${lead.value.heldBy ?? 'einem Admin'}, keine automatische Freigabe`;
  return `Automatische Freigabe am ${formatDate(lead.value.readyAt)}`;
});

const updating = ref(false);
const error = ref('');
const confirmOpen = ref(false);
const detailsOpen = ref(false);

function onRenamed(name: string) {
  const review = botId.value ? reviews[botId.value] : null;
  if (review?.bot) review.bot.name = name;
}

async function update(action: 'release' | 'hold' | 'unhold') {
  const id = botId.value;
  if (!id) return;

  updating.value = true;
  error.value = '';
  revision += 1;
  const result = await DemoService.updateLeadReview(id, action);
  if (result.status === 'OK') {
    reviews[id] = result.lead;
  } else {
    error.value = action === 'release'
      ? 'Die Freigabe hat nicht geklappt. Die Einladung wird automatisch noch einmal versucht.'
      : 'Das hat nicht geklappt, bitte versuch es noch einmal.';
    // The release may have happened even though inviting the lead failed
    await loadReview();
  }
  updating.value = false;
  confirmOpen.value = false;
}
</script>

<template>
  <template v-if="visible && lead">
    <!--
      A third grid column for the actions; on narrow screens they move under the text.
      `status`, not the Alert's `alert`: it's back on every page, screen readers needn't interrupt for it.
    -->
    <Alert
      variant="admin"
      role="status"
      data-cy="lead-review-banner"
      :data-status="lead.leadStatus"
      class="m-2 mb-0 w-auto md:has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr_auto]"
    >
      <UserRoundSearch />
      <AlertTitle class="line-clamp-none">
        Lead aus dem Funnel:
        <a v-if="lead.website.url" :href="lead.website.url" target="_blank" rel="noopener" class="underline underline-offset-2">
          {{ lead.companyName || lead.website.name || lead.website.url }}
        </a>
        <template v-if="goal"> · {{ goal }}</template>
      </AlertTitle>
      <AlertDescription>
        <p class="wrap-anywhere">
          {{ name }}<template v-if="lead.contact.email"> ({{ lead.contact.email }})</template> · {{ statusText }}
        </p>
        <p v-if="error" class="text-destructive">{{ error }}</p>
      </AlertDescription>

      <div class="col-start-2 mt-2 flex shrink-0 gap-2 md:col-start-3 md:row-span-2 md:row-start-1 md:mt-0 md:self-center">
        <Button
          data-cy="lead-review-details"
          variant="admin-outline"
          size="icon-sm"
          title="Lead-Details"
          aria-label="Lead-Details"
          @click="detailsOpen = true"
        >
          <Info />
        </Button>
        <Button
          v-if="lead.isPaused"
          data-cy="lead-review-unhold"
          variant="admin-outline"
          size="sm"
          :disabled="updating"
          @click="update('unhold')"
        >
          <Play /> Fortsetzen
        </Button>
        <Button
          v-else
          data-cy="lead-review-hold"
          variant="admin-outline"
          size="sm"
          :disabled="updating"
          @click="update('hold')"
        >
          <Pause /> Zurückhalten
        </Button>
        <Button
          v-if="lead.leadStatus === 'review'"
          data-cy="lead-review-release"
          variant="admin"
          size="sm"
          :disabled="updating"
          @click="confirmOpen = true"
        >
          <Send /> Jetzt freigeben
        </Button>
      </div>
    </Alert>

    <LeadDetailsDialog v-model="detailsOpen" :lead="lead" @renamed="onRenamed" />

    <LeadReleaseDialog v-model="confirmOpen" :lead="lead" :loading="updating" @confirm="update('release')" />
  </template>
</template>
